import { useEffect } from 'react';

import { mean } from 'lodash-es';

import type { HeroFeedItem } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { usePsfPromoteOrSharePageData } from 'src/app/pages/psf/promoteOrShare/pageContext';
import { useGuestInfo, usePageCopy } from 'src/app/pages/psf/promoteOrShare/shared/hooks';
import { useLoginState } from 'src/app/shared/hooks/session';
import { useTrackOnce } from 'src/app/shared/hooks/tracking';

type Result = ModelHookResult<{
	heading: string;
	subHeading1: string;
	subHeading2: string;
	showImpactBox: boolean;
	heroFeed: {
		items: readonly HeroFeedItem[];
		show: boolean;
	};
	pageTitle: string;
}>;

const MIN_FEED_ITEMS = 2;

export function usePageBody(): Result {
	const {
		data: { headlineText, headlineTextIdentified, subheadingText1, subheadingText2, progressBarLabelText },
	} = usePageCopy();
	const loginState = useLoginState();
	const hasGuestInfo = !!useGuestInfo();

	const pageTitle = progressBarLabelText;
	const {
		data: {
			petition: {
				promotionStats,
				feeds: { heroFeed },
			},
		},
	} = usePsfPromoteOrSharePageData();
	const trackOnce = useTrackOnce();

	const showFeed = heroFeed.length >= MIN_FEED_ITEMS;

	useEffect(() => {
		const promotionAmounts = heroFeed
			.map((item) => item.event)
			.filter((event) => event.__typename === 'HeroFeedPromotionEvent')
			.map((event) => event.amountUsd);
		void trackOnce('combo_feed_treated', {
			combo_feed_displayed: showFeed,
			...(showFeed
				? {
						combo_feed_item_count: heroFeed.length,
						combo_feed_photo_count: heroFeed.filter((item) => !!item.userPhoto).length,
						combo_feed_promotion_count: heroFeed.filter((item) => item.event.__typename === 'HeroFeedPromotionEvent')
							.length,
						combo_feed_share_count: heroFeed.filter((item) => item.event.__typename === 'HeroFeedShareEvent').length,
						combo_feed_amounts_usd: promotionAmounts,
						combo_feed_amounts_usd_mean: mean(promotionAmounts),
						combo_feed_shares_percent: 0,
					}
				: undefined),
		});
	}, [trackOnce]);

	const pageHeading = loginState === 'GUEST' && !hasGuestInfo ? headlineText : headlineTextIdentified;

	return {
		data: {
			heading: pageHeading,
			subHeading1: subheadingText1,
			subHeading2: subheadingText2,
			showImpactBox: promotionStats.signatureCount > 1,
			heroFeed: {
				items: heroFeed,
				show: showFeed,
			},
			pageTitle,
		},
	};
}
